import { useState } from 'react';
import { Modal } from 'antd';
import del from '../../../assets/images/svg/delete-icon.svg';
import pen from '../../../assets/images/svg/pen-icon.svg';
import { toast } from 'react-toastify';
import { SelectedImagesModal } from '../modals/selected_images_modal';
import { EDIT_MODAL, DELETE_MODAL } from '../constants';
import styled from 'styled-components';
import { ImageType } from '../../../data/types/files';
import { Icon } from '@iconify/react';

type ImageLibraryButtonsProps = {
  filesRepository: any;
  alts_modal: boolean;
  toggleAltsModal: () => void;
  edit_able_images: ImageType[];
  toggleDone: () => void;
  store_id?: string;
  ownedLibrary: boolean;
  images: any;
  getData: () => void;
  setImages: (images: ImageType[]) => void;
  setEditableImages: (images: ImageType[]) => void;
};

export function ImageLibraryButtons({
  filesRepository,
  alts_modal,
  toggleAltsModal,
  edit_able_images,
  toggleDone,
  getData,
  store_id,
  ownedLibrary,
  images,
  setImages,
  setEditableImages,
}: ImageLibraryButtonsProps) {
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editedImages, setEditedImages] = useState<ImageType[]>([]);
  const [selected_image, setSelectedImage] = useState<ImageType>();
  const [selected_index, setSelectedIndex] = useState(0);

  const confirmEditModal = async () => {
    const api_call = await filesRepository.addAltsToStoreImages({
      images: editedImages,
      store_id: store_id,
    });

    const api_call_2 = await filesRepository.addAltsToStoreImages({
      images: [selected_image],
      store_id: store_id,
    });
    if ((api_call_2.length > 0 && api_call_2[0]?.error) || api_call[0]?.error) {
      toast.warn('Error Editing Images');
    }
    getData();
    closeModals();
  };
  const confirmDeleteModal = async () => {
    const api_call: { deleted: boolean; id: number; error?: string }[] =
      await toast.promise(
        filesRepository.removeStoreImages({
          images: edit_able_images,
          store_id: store_id,
        }),
        {
          pending: 'Deleting images',
          error: 'Error deleting images',
        },
      );
    if (api_call.some((item) => !item.deleted)) {
      const imagesWithErrorSet = new Set(
        api_call.filter((item) => !item.deleted).map((item) => item.id),
      );
      const editableImagesSet = new Set(
        edit_able_images.map((item) => item.id),
      );
      for (let i = 0; i < api_call.length; i++) {
        if (!api_call[i].deleted) {
          toast.warn(api_call[i].error);
        }
      }
      const imagesToRemove = new Set(
        images
          .filter((img: ImageType) => {
            return (
              // Only remove images that have no errors and are selected
              !imagesWithErrorSet.has(img.id) && editableImagesSet.has(img.id)
            );
          })
          .map((img: ImageType) => img.id),
      );
      setImages(
        images.filter((image: ImageType) => !imagesToRemove.has(image.id)),
      );
      setEditableImages([]);
      closeModals(false, false);
    } else {
      toast.success('Images deleted successfully, please refresh the page');
      getData();
      closeModals(false, true);
    }
  };
  const closeModals = (remove_selection = false, isDelete = false) => {
    if (remove_selection) {
      toggleDone();
    }
    setSelectedIndex(0);
    setEditedImages([]);
    setDeleteModal(false);
    setEditModal(false);
    if (isDelete) {
      setImages(
        images.filter((image: ImageType) => !edit_able_images.includes(image)),
      );
    }
  };
  const openModal = (activeModal: boolean) => {
    if (edit_able_images.length > 0) {
      setSelectedImage(edit_able_images[selected_index || 0]);
      if (activeModal) {
        setDeleteModal(true);
      } else {
        setEditModal(true);
      }
    } else {
      toast.warn('Please select at least image to edit');
    }
  };

  const next = async (is_modal_delete: boolean) => {
    if (selected_image) {
      setEditedImages([...editedImages, selected_image]);
      if (edit_able_images[selected_index + 1]) {
        setSelectedIndex(selected_index + 1);
        setSelectedImage(edit_able_images[selected_index + 1]);
      } else if (is_modal_delete) {
        confirmDeleteModal();
        return;
      } else {
        confirmEditModal();
        return;
      }
      if (edit_able_images.length === selected_index + 1) {
        if (is_modal_delete) {
          confirmDeleteModal();
        } else {
          confirmEditModal();
        }
      }
    }
  };

  return (
    <>
      <Wrapper>
        {ownedLibrary && (
          <>
            <SelectedImagesModal
              setSelectedImage={setSelectedImage}
              closeModals={closeModals}
              edit_able_images={edit_able_images}
              selected_index={selected_index}
              selected_image={selected_image}
              next={next}
              is_modal_delete={false}
              current_modal={EDIT_MODAL}
              is_open={editModal}
            />

            {/* delete */}
            <SelectedImagesModal
              setSelectedImage={setSelectedImage}
              closeModals={closeModals}
              edit_able_images={edit_able_images}
              selected_index={selected_index}
              selected_image={selected_image}
              next={next}
              is_modal_delete={true}
              current_modal={DELETE_MODAL}
              is_open={deleteModal}
            />
          </>
        )}
        <div>
          <div style={{ display: 'flex' }}>
            {ownedLibrary && (
              <div
                className="edit-button ml-2"
                onClick={() => openModal(false)}
                title="Edit alt for selected images"
              >
                <Icon
                  icon="basil:edit-outline"
                  width="40"
                  height="40"
                  style={{ color: '#287d06' }}
                />
              </div>
            )}
            {alts_modal && (
              <div
                className={`${alts_modal ? 'active' : ''}  missing-alts-button`}
                onClick={() => toggleAltsModal()}
                title="Show images with missing alt text only"
              >
                <Icon
                  icon="material-symbols-light:warning-off-outline"
                  width="40"
                  height="40"
                  style={{ color: 'orange' }}
                />
              </div>
            )}
            {!alts_modal && (
              <div
                className={`${alts_modal ? 'active' : ''}  missing-alts-button`}
                onClick={() => toggleAltsModal()}
                title="Show images with missing alt text only"
              >
                <Icon
                  icon="material-symbols-light:warning-outline"
                  width="40"
                  height="40"
                  style={{ color: 'orange' }}
                />
              </div>
            )}
            {ownedLibrary && (
              <div
                className="del-button"
                onClick={() => openModal(true)}
                title="Delete selected images"
              >
                <Icon
                  icon="fluent:delete-32-regular"
                  width="40"
                  height="40"
                  style={{ color: '#c71f1f' }}
                />
              </div>
            )}
          </div>
        </div>
      </Wrapper>
    </>
  );
}

const Wrapper = styled.div`
  .edit-button {
    color: var(--app-color-dark);
    border-radius: 3px;
    width: 36px;
    margin-left: 10px;
    height: 36px;
    display: flex;
    cursor: pointer;
    justify-content: center;
    align-items: center;
  }
  .del-button {
    color: var(--color-del);
    border-radius: 3px;
    margin-left: 20px;
    cursor: pointer;
    width: 36px;
    height: 36px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .missing-alts-button {
    /* background: #FF8F73; */
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: 500;
    margin-left: 20px;
    font-size: 28px;
    border-radius: 3px;
    cursor: pointer;
    width: 36px;
    height: 36px;
  }
  .active.missing-alts-button {
  }
`;
